import { useEffect } from 'react';

import { reportMykissClientError } from '@lib/errors/client';
import { getNodesFromConnection } from '@graphql/connections/getNodesFromConnection';
import { UseUserInfo, useUserInfo } from '@domain/user/useUserInfo';
import { getCatchDetails } from '@graphql/queries/rutilus/Catch';
import type { CatchConditions } from '../../../types/CatchConditions';
import type { CatchGearItem } from '../../../types/CatchGearItem';
import type { CatchPost } from '../../../types/CatchPost';
import type { Trip } from '../../../types/Trip';
import { useLazyQuery } from '@apollo/client/react';
import { useAuth } from '@domain/auth/AuthContext';

export interface CatchDetails {
  catchConditions: CatchConditions | null | undefined;
  catchGear: CatchGearItem[];
  catchPost: CatchPost | null | undefined;
  currentUser: UseUserInfo;
  trip: Trip | null | undefined;
}

interface CatchDetailsData {
  catchDetails: CatchDetails;
  isLoading: boolean;
}

export const useCatchDetails = (currentPostId: string | undefined): CatchDetailsData => {
  const currentUser = useUserInfo();
  const { getToken } = useAuth();
  const { isLoggedIn, isLoadingUser } = currentUser;

  const [fetchCatchDetails, { data, loading: isCatchDetailsLoading }] =
    useLazyQuery(getCatchDetails);

  useEffect(() => {
    fetchCatchDetails({
      variables: { externalId: currentPostId },
      context: { getAuthToken: getToken },
    }).catch(reportMykissClientError);
  }, [currentPostId, currentUser.isPremium, fetchCatchDetails, getToken]);

  const catchDetails = data?.catchDetails;
  const catchConditions = isLoggedIn ? catchDetails?.catchConditions : undefined;
  const catchPost = catchDetails?.catchPost;
  const trip = catchDetails?.trip;
  const catchGear = getNodesFromConnection(catchDetails?.catchGear);
  const isLoading = isLoadingUser || isCatchDetailsLoading;

  return {
    catchDetails: { catchGear, trip, currentUser, catchPost, catchConditions },
    isLoading,
  };
};
